import { useEffect } from 'react';
import { CopyId } from '@bb/ui';
import type { CampaignView } from '../../../api/client.ts';
import { DELIVERY_LABELS } from '../labels.ts';
import { DeliveryControlsForm } from '../schedule/DeliveryControlsForm.tsx';
import { ScheduledDeliveryForm } from '../schedule/ScheduledDeliveryForm.tsx';
import { ActionBasedForm } from '../trigger/ActionBasedForm.tsx';
import type { CampaignWizardDraft, WizardIssue } from './steps.ts';

export interface ScheduleStepProps {
  environmentId: string;
  campaign: CampaignView;
  draft: CampaignWizardDraft;
  onChange: (next: CampaignWizardDraft) => void;
  issues: WizardIssue[];
  readOnly: boolean;
  canManageExemptions: boolean;
  saveError?: unknown;
}

/**
 * Schedule (BB-97): when the campaign sends — a schedule (BB-99), a trigger (BB-100) or the server API (BB-101), by
 * its delivery type, which is fixed at creation — and its delivery controls (re-eligibility, quiet hours, rate,
 * exemptions; BB-102).
 */
export function ScheduleStep({ environmentId, campaign, draft, onChange, issues, readOnly, canManageExemptions, saveError }: ScheduleStepProps) {
  const { schedule } = draft.content;
  const setSchedule = (next: CampaignWizardDraft['content']['schedule']) => onChange({ ...draft, content: { ...draft.content, schedule: next } });
  // The schedule form shows «as soon as it launches» for a schedule not chosen yet: the draft takes what is shown.
  const unset = campaign.deliveryType === 'scheduled' && schedule.scheduled === undefined && !readOnly;
  useEffect(() => {
    if (unset) setSchedule({ ...schedule, scheduled: { kind: 'immediate' } });
    // Only when the step opens on a schedule not chosen yet (the draft is read at that moment).
  }, [unset]);
  const stepErrors = issues.filter((i) => i.severity === 'error' && (i.path === 'schedule.scheduled' || i.path === 'schedule.actionBased'));
  return (
    <section aria-label="Schedule" className="canvas-wizard__panel">
      <h2>Schedule</h2>
      <p className="hint">
        Delivery type: <strong>{DELIVERY_LABELS[campaign.deliveryType]}</strong>. It is chosen when the campaign is created; duplicate the campaign to send it another way.
      </p>
      {stepErrors.map((i) => (
        <p key={i.message} className="bb-field__error" role="alert">
          {i.message}
        </p>
      ))}
      {campaign.deliveryType === 'scheduled' && <ScheduledDeliveryForm environmentId={environmentId} value={schedule.scheduled} readOnly={readOnly} readOnlyHint="You can view this campaign but not change it." onChange={(scheduled) => setSchedule({ ...schedule, scheduled })} />}
      {campaign.deliveryType === 'action_based' && <ActionBasedForm environmentId={environmentId} value={schedule.actionBased} readOnly={readOnly} onChange={(actionBased) => setSchedule({ ...schedule, actionBased })} />}
      {campaign.deliveryType === 'api_triggered' && (
        <div className="stack-form">
          <p>Your server sends this campaign through the campaign trigger API, naming it by its API id; the request can carry trigger properties for the message.</p>
          <CopyId label="Campaign API id" value={campaign.apiId} />
        </div>
      )}
      <DeliveryControlsForm
        environmentId={environmentId}
        category={campaign.category}
        value={{ deliveryControls: draft.content.deliveryControls, exemptions: draft.content.exemptions }}
        canManageExemptions={canManageExemptions}
        readOnly={readOnly}
        saveError={saveError}
        onChange={(next) => onChange({ ...draft, content: { ...draft.content, deliveryControls: next.deliveryControls, exemptions: next.exemptions } })}
      />
    </section>
  );
}
